import {
  BigUIntType,
  BinaryCodec,
  FieldDefinition,
  ListType,
  Struct,
  StructType,
  U32Type,
  U64Type,
} from "@multiversx/sdk-core";
import { BigNumber } from "bignumber.js";
import { NUTS_STAKING_CONSTANTS } from "@/contracts/nutsStaking";
import { MEME_NFT_POST, TOP_MEME_NFT_POST } from "@/helpers/elrondApi";
import { Address } from "@multiversx/sdk-core/out";

export interface NFT_STAKED_POSITION {
  nonce: number;
  stakedBlock: number;
  rewardPerShare: any;
  nftNonces: number[];
  nbNfts: number;
  baseStruct: Struct;
}

export interface STAKED_POSITION {
  amount: BigNumber;
  nonce: number;
  stakedBlock: number;
  rewardPerShare: any;
  lockingPeriod: number;
  unstakedBlock: number;
  withdrawable: BigNumber;
  baseStruct: Struct | null;
}

class TransformHelper {
  public async mapStakedPosition(nft: any, currentBlock: number): Promise<STAKED_POSITION> {
    const attributes = Buffer.from(nft.attributes, "base64");
    const codec = new BinaryCodec();

    const structType = new StructType('StakingFarmTokenAttributes', [
      new FieldDefinition('staked_block', '', new U64Type()),
      new FieldDefinition('reward_per_share', '', new BigUIntType()),
      new FieldDefinition('locking_period', '', new U64Type()),
      new FieldDefinition('unstaked_block', '', new U64Type()),
    ]);

    const [decoded] = codec.decodeNested(attributes, structType);
    const stakedPosition = decoded.valueOf();

    const stakedBlock = stakedPosition.staked_block.toNumber();
    const lockingPeriod = stakedPosition.locking_period.toNumber();
    const endBlockNonce = stakedBlock + lockingPeriod;

    let withdrawable = new BigNumber(nft.balance);

    if (currentBlock <= endBlockNonce) {
      let unstakedBlock = stakedPosition.unstaked_block.toNumber();

      let initialStakedAmount;
      if (unstakedBlock === 0) {
        initialStakedAmount = new BigNumber(nft.balance);
      } else {
        initialStakedAmount = new BigNumber(nft.balance).multipliedBy(lockingPeriod, 10).dividedToIntegerBy((endBlockNonce - unstakedBlock), 10);
      }

      if (unstakedBlock === 0) {
        unstakedBlock = stakedBlock;
      }

      if (currentBlock <= unstakedBlock + NUTS_STAKING_CONSTANTS.MINIMUM_UNSTAKE_TIME) {
        withdrawable = new BigNumber('0');
      } else {
        withdrawable = initialStakedAmount.multipliedBy((currentBlock - unstakedBlock), 10).dividedToIntegerBy(lockingPeriod, 10);
      }
    }

    return {
      amount: new BigNumber(nft.balance),
      nonce: nft.nonce,
      stakedBlock,
      rewardPerShare: stakedPosition.reward_per_share.toFixed(),
      baseStruct: decoded as Struct,
      lockingPeriod,
      withdrawable,
      unstakedBlock: stakedPosition.unstaked_block.toNumber(),
    };
  }

  // NFT staking farm tokens (nft-nuts-staking / nft-vip-staking). The VIP variant's attributes
  // carry an extra `nb_nfts` field; the plain NUTS variant does not, so `nbNfts` is derived from
  // the nonce list length instead.
  public mapNftStakedPosition(hasNbNfts: boolean) {
    return async (nft: any, _currentBlock: number): Promise<NFT_STAKED_POSITION> => {
      const attributes = Buffer.from(nft.attributes, "base64");
      const codec = new BinaryCodec();

      const fields = [
        new FieldDefinition('staked_block', '', new U64Type()),
        new FieldDefinition('reward_per_share', '', new BigUIntType()),
        new FieldDefinition('nft_nonces', '', new ListType(new U64Type())),
      ];

      if (hasNbNfts) {
        fields.push(new FieldDefinition('nb_nfts', '', new U32Type()));
      }

      const structType = new StructType('StakingFarmTokenAttributes', fields);

      const [decoded] = codec.decodeNested(attributes, structType);
      const position = decoded.valueOf();

      const nftNonces: number[] = (position.nft_nonces || []).map((nonce: any) => nonce.toNumber());

      return {
        nonce: nft.nonce,
        stakedBlock: position.staked_block.toNumber(),
        rewardPerShare: position.reward_per_share.toFixed(),
        nftNonces,
        nbNfts: hasNbNfts ? position.nb_nfts.toNumber() : nftNonces.length,
        baseStruct: decoded as Struct,
      };
    };
  }

  public mapMeme(nft: any): MEME_NFT_POST {
    const attributes = Buffer.from(nft.attributes, "base64");

    const leftPeriod = attributes.readUInt32BE(0);
    const rightPeriod = attributes.readUInt32BE(4);
    const period = 2 ** 32 * leftPeriod + rightPeriod;

    const length = attributes.readUInt32BE(8);
    const category = attributes.toString(null, 12, 12 + length);
    const creator = new Address(attributes.slice(12 + length)).bech32();

    return {
      name: nft.name,
      uri: nft.url,
      nonce: nft.nonce,
      owner: nft.owner,
      creator,
      period,
      category,
      rarity: 0,
    };
  }

  public mapTopMeme(nft: any): TOP_MEME_NFT_POST {
    const attributes = Buffer.from(nft.attributes, "base64");

    const rarity = attributes.readUInt8();

    const leftOriginalNonce = attributes.readUInt32BE(1);
    const rightOriginalNonce = attributes.readUInt32BE(5);
    const originalNonce = 2 ** 32 * leftOriginalNonce + rightOriginalNonce;

    const leftPeriod = attributes.readUInt32BE(9);
    const rightPeriod = attributes.readUInt32BE(13);
    const period = 2 ** 32 * leftPeriod + rightPeriod;

    const length = attributes.readUInt32BE(17);
    const category = attributes.toString(null, 21, 21 + length);
    const creator = new Address(attributes.slice(21 + length)).bech32();

    return {
      name: nft.name,
      uri: nft.url,
      nonce: originalNonce,
      owner: nft.owner,
      creator,
      period,
      category,
      rarity,
      topNonce: nft.nonce,
    };
  }
}

const transformHelper = new TransformHelper();

export default transformHelper;
